/**
 * The picture guide for everyone who is not at the till all day: managers,
 * stock controllers and owners.
 *
 * Same rules as the cashier's guide (tasks.tsx): one job a tile, one short
 * sentence a step, the button named exactly as the screen shows it, and a
 * picture of the whole screen with the step's part lit up.
 */

import {
  ArrowUpRight,
  BookUser,
  ChartColumn,
  CheckCheck,
  ClipboardCheck,
  ClipboardList,
  Ellipsis,
  FileSpreadsheet,
  HandCoins,
  KeyRound,
  MonitorSmartphone,
  Package,
  PackagePlus,
  PackageX,
  Pencil,
  ReceiptText,
  RefreshCw,
  ShieldCheck,
  ShieldOff,
  SlidersHorizontal,
  Smartphone,
  Tag,
  TrendingDown,
  Truck,
  Upload,
  UserMinus,
  UserPlus,
  Wallet,
} from "lucide-react";
import type { ReactNode } from "react";

import { cn } from "@/lib/cn";

import { Basket, Big, Btn, Chip, Field, Keypad, Note, PinDots, Row, Tap, Verdict } from "./scene";
import type { Task } from "./tasks";
import type { FormWhere, OfficePage } from "./tillmap";

// ── The pages these jobs happen on ──────────────────────────────────────────

const SETTINGS_TABS = ["My profile", "Business", "Branches & sections", "Users", "Tills & devices"];

const INVENTORY: OfficePage = {
  nav: "Inventory",
  title: "Inventory",
  actions: [
    { id: "shared", label: "Add from the shared list" },
    { id: "import", label: "Import products" },
  ],
  kpis: 4,
  filters: ["Search", "Category", "Stock status"],
  rows: 4,
  rowAction: { label: "⋯" },
  panel: "Top low stock items",
};
const INVENTORY_EDIT: OfficePage = { ...INVENTORY, rowAction: { label: "✎" } };

const USERS: OfficePage = {
  nav: "Settings",
  title: "Settings",
  tabs: SETTINGS_TABS,
  tab: "Users",
  actions: [{ id: "add", label: "Add someone", tone: "brand" }],
  rows: 5,
  rowAction: { label: "Set PIN" },
};
const USERS_LEAVING: OfficePage = { ...USERS, rowAction: { label: "Deactivate", tone: "danger" } };

const DEVICES: OfficePage = {
  nav: "Settings",
  title: "Settings",
  tabs: SETTINGS_TABS,
  tab: "Tills & devices",
  actions: [{ id: "register", label: "Register a terminal", tone: "brand" }],
  rows: 3,
  rowAction: { label: "Suspend", tone: "danger" },
};

const BUSINESS: OfficePage = {
  nav: "Settings",
  title: "Settings",
  tabs: SETTINGS_TABS,
  tab: "Business",
  actions: [{ id: "save", label: "Save changes", tone: "brand" }],
  rows: 4,
  panel: "Trading rules",
};

const CUSTOMERS: OfficePage = {
  nav: "Customers",
  title: "Customers",
  actions: [
    { id: "book", label: "On the book" },
    { id: "add", label: "Add customer", tone: "brand" },
  ],
  filters: ["Name or phone number"],
  rows: 6,
};

const ORDERS_TO_RECEIVE: OfficePage = {
  nav: "Procurement",
  title: "Procurement",
  tabs: ["Purchase orders", "Deliveries"],
  tab: "Purchase orders",
  rows: 4,
  rowAction: { label: "Receive", tone: "brand" },
};
const ORDERS_TO_APPROVE: OfficePage = {
  ...ORDERS_TO_RECEIVE,
  rowAction: { label: "Approve", tone: "brand" },
};

const REPORTS: OfficePage = {
  nav: "Reports",
  title: "Reports",
  filters: ["From", "To", "This month"],
  rows: 6,
  rowAction: { label: "Spreadsheet" },
};

const EXPENSES: OfficePage = {
  nav: "Expenses",
  title: "Expenses",
  actions: [{ id: "record", label: "Record a cost", tone: "brand" }],
  filters: ["Month"],
  rows: 5,
  rowAction: { label: "Reverse" },
};

const DASHBOARD: OfficePage = {
  nav: "Dashboard",
  title: "Dashboard",
  kpis: 4,
  rows: 3,
  panel: "Best sellers",
};

function form(
  over: OfficePage | "till",
  title: string,
  fields: FormWhere["fields"],
  buttons: FormWhere["buttons"],
) {
  return (focus: string): FormWhere => ({ screen: "form", over, title, fields, buttons, focus });
}

const office = (page: OfficePage, focus: string) => ({ screen: "office" as const, page, focus });

// ── Close-up pieces the back office needs ───────────────────────────────────

/** A menu item, as it looks down the left of the back office. */
function NavItem({ label }: { label: string }) {
  return (
    <span className="inline-flex h-11 items-center rounded-control bg-brand-500 px-5 font-semibold text-white shadow-card">
      {label}
    </span>
  );
}

/** A tab along the top of Settings. */
function TabPill({ label }: { label: string }) {
  return (
    <span className="inline-flex rounded-control bg-canvas p-1">
      <span className="inline-flex h-9 items-center rounded-md bg-surface px-4 font-semibold text-ink-900 shadow-card">
        {label}
      </span>
    </span>
  );
}

/** The ⋯ menu on a product, open, with one item tapped. */
function ProductMenu({ pick }: { pick: string }) {
  return (
    <div className="grid w-52 gap-0.5 rounded-card border border-line bg-surface p-1.5 text-left text-sm shadow-pop">
      {["Receive stock", "Adjust stock", "Count stock", "View movements"].map((item) =>
        item === pick ? (
          <Tap key={item}>
            <span className="block w-full rounded-md bg-brand-50 px-3 py-2 font-semibold text-brand-600">
              {item}
            </span>
          </Tap>
        ) : (
          <span key={item} className="block px-3 py-2 text-ink-600">
            {item}
          </span>
        ),
      )}
    </div>
  );
}

/** One row of a table, with what matters in it. */
function TableRow({
  cells,
  tone,
  children,
}: {
  cells: string[];
  tone?: "warn" | "danger";
  children?: ReactNode;
}) {
  return (
    <div
      className={cn(
        "flex w-full max-w-[19rem] items-center justify-between gap-3 rounded-card border border-line bg-surface px-3 py-2.5 text-sm",
        tone === "warn" && "border-warn-500 bg-warn-50",
        tone === "danger" && "border-danger-500 bg-danger-50",
      )}
    >
      <span className="grid text-left">
        <span className="font-semibold text-ink-900">{cells[0]}</span>
        {cells[1] && <span className="text-ink-500">{cells[1]}</span>}
      </span>
      {children ??
        (cells[2] && <span className="font-numeric font-semibold text-ink-900">{cells[2]}</span>)}
    </div>
  );
}

function Toggle() {
  return (
    <span className="inline-flex items-center gap-3 rounded-card bg-surface px-4 py-3 shadow-card">
      <Tap>
        <span className="relative inline-block h-7 w-12 rounded-full bg-brand-500">
          <span className="absolute right-1 top-1 size-5 rounded-full bg-white" />
        </span>
      </Tap>
      <span className="text-sm font-semibold text-ink-900">Refunds need manager approval</span>
    </span>
  );
}

// ── Manager ─────────────────────────────────────────────────────────────────

const CASH_OUT = form(
  "till",
  "Cash out",
  [
    { id: "amount", label: "Amount (R)", value: "200.00" },
    { id: "what", label: "What is it for?", value: "Paid the bread delivery" },
  ],
  [{ id: "record", label: "Record cash out", tone: "brand" }],
);

const SET_PIN = form(
  USERS,
  "Set PIN",
  [{ id: "pin", label: "New PIN", kind: "pin" }],
  [
    { id: "set", label: "Set PIN", tone: "brand" },
    { id: "cancel", label: "Cancel" },
  ],
);

const EDIT_PRODUCT = form(
  INVENTORY_EDIT,
  "Edit product",
  [
    { id: "name", label: "Product name", value: "Coca-Cola 500ml" },
    { id: "price", label: "Price", value: "13.00" },
    { id: "why", label: "Why is the price changing?", value: "Supplier went up" },
  ],
  [{ id: "save", label: "Save changes", tone: "brand" }],
);

const CUSTOMER_PAYMENT = form(
  CUSTOMERS,
  "Mrs Dlamini",
  [
    { id: "paid", label: "Payment received", value: "200.00" },
    { id: "limit", label: "Credit limit", value: "500.00" },
    { id: "reason", label: "Reason", value: "Paid at the counter" },
  ],
  [{ id: "take", label: "Take payment", tone: "brand" }],
);

export const MANAGER_TASKS: Task[] = [
  {
    id: "approve-refund",
    title: { en: "Approve a refund" },
    icon: ShieldCheck,
    tone: "brand",
    steps: [
      {
        words: { en: "The cashier calls you. Check the item and the receipt." },
        where: { screen: "refund", focus: "lines" },
        picture: <Basket lines={[["Coca-Cola 500ml", 1, "R12.00"]]} total="R12.00" />,
      },
      {
        words: { en: "Type your own staff code." },
        where: { screen: "refund", focus: "manager" },
        picture: <Field label="Manager's staff code" value="SIPHO" />,
      },
      {
        words: { en: "Type your PIN yourself. Never tell the cashier." },
        where: { screen: "refund", focus: "manager" },
        picture: (
          <div className="grid justify-items-center gap-3">
            <PinDots filled={3} />
            <Keypad pressed="4" />
          </div>
        ),
      },
      {
        words: { en: "The cashier taps Refund. Your name is on it." },
        where: { screen: "refund", focus: "button" },
        picture: (
          <Tap>
            <Btn variant="primary" size="pos">
              Refund R12.00
            </Btn>
          </Tap>
        ),
      },
    ],
  },
  {
    id: "cash-out",
    title: { en: "Money out of the drawer" },
    icon: ArrowUpRight,
    tone: "brand",
    steps: [
      {
        words: { en: "Paid someone from the drawer? Tap Cash out." },
        where: { screen: "till", focus: "shiftbar" },
        picture: (
          <Tap>
            <Btn variant="ghost" icon={<ArrowUpRight className="size-4" />}>
              Cash out
            </Btn>
          </Tap>
        ),
      },
      {
        words: { en: "Type the amount." },
        where: CASH_OUT("amount"),
        picture: <Field label="Amount (R)" value="200.00" />,
      },
      {
        words: { en: "Say what it was for." },
        where: CASH_OUT("what"),
        picture: <Field label="What is it for?" value="Paid the bread delivery" wide />,
      },
      {
        words: { en: "Tap Record cash out. Cash-up will not be short." },
        where: CASH_OUT("record"),
        picture: (
          <Tap>
            <Btn variant="primary">Record cash out</Btn>
          </Tap>
        ),
      },
    ],
  },
  {
    id: "check-cash-up",
    title: { en: "Check a cash-up" },
    icon: ClipboardCheck,
    tone: "brand",
    steps: [
      {
        words: { en: "Short or over? Ask the cashier why." },
        where: { screen: "shift-closed", focus: "result" },
        picture: (
          <span className="rounded-card bg-warn-50 px-5 py-3 text-xl font-bold text-warn-700">
            Short by R20.00
          </span>
        ),
      },
      {
        words: { en: "Look for money paid out and not recorded." },
        picture: (
          <Row>
            <Note rand={20} />
            <Big tone="warn">
              <ReceiptText />
            </Big>
          </Row>
        ),
      },
      {
        words: { en: "Happy with the reason? Tap Approve the difference." },
        where: { screen: "shift-closed", focus: "approve" },
        picture: (
          <Tap>
            <Btn variant="primary">Approve the difference</Btn>
          </Tap>
        ),
      },
    ],
  },
  {
    id: "unlock-pin",
    title: { en: "Unlock a PIN" },
    icon: KeyRound,
    tone: "brand",
    steps: [
      {
        words: { en: "Open Settings. Tap Users." },
        where: office(USERS, "tab:Users"),
        picture: (
          <Tap>
            <TabPill label="Users" />
          </Tap>
        ),
      },
      {
        words: { en: "Find the person. Tap Unlock or Set PIN." },
        where: office(USERS, "row-action"),
        picture: (
          <TableRow cells={["Thabo Nkosi", "PIN locked"]} tone="danger">
            <Tap>
              <Btn size="sm" icon={<KeyRound className="size-4" />}>
                Unlock
              </Btn>
            </Tap>
          </TableRow>
        ),
      },
      {
        words: { en: "Let them type their new PIN. Do not look." },
        where: SET_PIN("pin"),
        picture: (
          <div className="grid justify-items-center gap-3">
            <PinDots filled={4} />
            <Keypad />
          </div>
        ),
      },
      {
        words: { en: "Tap Set PIN. They can sign in again." },
        where: SET_PIN("set"),
        picture: (
          <Tap>
            <Btn variant="primary">Set PIN</Btn>
          </Tap>
        ),
      },
    ],
  },
  {
    id: "change-price",
    title: { en: "Change a price" },
    icon: Tag,
    tone: "brand",
    steps: [
      {
        words: { en: "Open Inventory." },
        where: office(INVENTORY_EDIT, "nav"),
        picture: (
          <Tap>
            <NavItem label="Inventory" />
          </Tap>
        ),
      },
      {
        words: { en: "Find the product. Tap the pencil." },
        where: office(INVENTORY_EDIT, "row-action"),
        picture: (
          <TableRow cells={["Coca-Cola 500ml", "R12.00"]}>
            <Tap>
              <Btn size="sm" icon={<Pencil className="size-4" />} aria-label="Edit" />
            </Tap>
          </TableRow>
        ),
      },
      {
        words: { en: "Type the new price." },
        where: EDIT_PRODUCT("price"),
        picture: <Field label="Price" value="13.00" />,
      },
      {
        words: { en: "Say why it changed." },
        where: EDIT_PRODUCT("why"),
        picture: <Field label="Why is the price changing?" value="Supplier went up" wide />,
      },
      {
        words: { en: "Tap Save changes. The tills get the new price." },
        where: EDIT_PRODUCT("save"),
        picture: (
          <Tap>
            <Btn variant="primary">Save changes</Btn>
          </Tap>
        ),
      },
    ],
  },
  {
    id: "account-payment",
    title: { en: "Account payment" },
    icon: HandCoins,
    tone: "brand",
    steps: [
      {
        words: { en: "Open Customers. Tap the customer." },
        where: office(CUSTOMERS, "rows"),
        picture: (
          <Tap>
            <TableRow cells={["Mrs Dlamini", "072 555 0134", "R350.00"]} />
          </Tap>
        ),
      },
      {
        words: { en: "Type what they paid." },
        where: CUSTOMER_PAYMENT("paid"),
        picture: <Field label="Payment received" value="200.00" />,
      },
      {
        words: { en: "Tap Take payment. What they owe goes down." },
        where: CUSTOMER_PAYMENT("take"),
        picture: (
          <Tap>
            <Btn variant="primary">Take payment</Btn>
          </Tap>
        ),
      },
    ],
  },
  {
    id: "tills-sending",
    title: { en: "Are the tills sending?" },
    icon: RefreshCw,
    tone: "brand",
    steps: [
      {
        words: { en: "Open Settings. Tap Tills & devices." },
        where: office(DEVICES, "tab:Tills & devices"),
        picture: (
          <Tap>
            <TabPill label="Tills & devices" />
          </Tap>
        ),
      },
      {
        words: { en: "Look at Queued. A big number means not sent." },
        where: office(DEVICES, "rows"),
        picture: (
          <div className="grid w-full max-w-[19rem] gap-2">
            <TableRow cells={["Front counter", "Queued"]}>
              <span className="font-numeric font-semibold text-money-700">0</span>
            </TableRow>
            <TableRow cells={["Takeaway", "Queued"]} tone="warn">
              <span className="font-numeric text-lg font-bold text-warn-700">42</span>
            </TableRow>
          </div>
        ),
      },
      {
        words: { en: "Get that till signal. It sends by itself." },
        picture: <Chip online />,
      },
    ],
  },
];

// ── Stock controller ────────────────────────────────────────────────────────

const RECEIVE_ORDER = form(
  ORDERS_TO_RECEIVE,
  "Receive PO-0012",
  [
    { id: "line1", label: "Coca-Cola 500ml · ordered 24", value: "24" },
    { id: "line2", label: "White bread · ordered 30", value: "28" },
    { id: "counter", label: "Which counter does it go to?", value: "Grocery" },
    { id: "invoice", label: "Their invoice or delivery note number", value: "INV-5521" },
  ],
  [{ id: "receive", label: "Receive R1 284.00", tone: "brand" }],
);

const RECEIVE_STOCK = form(
  INVENTORY,
  "Receive stock",
  [
    { id: "counter", label: "Counter", value: "Grocery" },
    { id: "qty", label: "Quantity received", value: "24" },
    { id: "cost", label: "Cost per unit", value: "9.50" },
    { id: "ref", label: "Reference", value: "Cash & carry slip" },
  ],
  [{ id: "go", label: "Receive", tone: "brand" }],
);

const ADJUST = form(
  INVENTORY,
  "Adjust stock",
  [
    { id: "counter", label: "Counter", value: "Grocery" },
    { id: "change", label: "Change in quantity", value: "-2" },
    { id: "type", label: "Type", value: "Wastage, breakage or expiry" },
    { id: "reason", label: "Reason", value: "Dropped crate" },
  ],
  [{ id: "adjust", label: "Adjust", tone: "brand" }],
);

const COUNT = form(
  INVENTORY,
  "Count stock",
  [
    { id: "counter", label: "Counter", value: "Grocery" },
    { id: "counted", label: "Counted on the shelf", value: "17" },
  ],
  [{ id: "save", label: "Save the count", tone: "brand" }],
);

export const STOCK_TASKS: Task[] = [
  {
    id: "receive-delivery",
    title: { en: "Receive a delivery" },
    icon: Truck,
    tone: "money",
    steps: [
      {
        words: { en: "Count the delivery before the driver leaves." },
        picture: (
          <Big tone="money">
            <Package />
          </Big>
        ),
      },
      {
        words: { en: "Open Procurement. Tap Receive on the order." },
        where: office(ORDERS_TO_RECEIVE, "row-action"),
        picture: (
          <TableRow cells={["PO-0012", "Coca-Cola SA · 2 lines"]}>
            <Tap>
              <Btn size="sm" variant="primary">
                Receive
              </Btn>
            </Tap>
          </TableRow>
        ),
      },
      {
        words: { en: "Type how many really came." },
        where: RECEIVE_ORDER("line1"),
        picture: (
          <div className="grid w-full max-w-[19rem] gap-2">
            <TableRow cells={["Coca-Cola 500ml", "ordered 24"]}>
              <span className="grid h-9 w-14 place-items-center rounded-control border-2 border-brand-500 bg-surface font-numeric font-semibold">
                24
              </span>
            </TableRow>
            <TableRow cells={["White bread", "ordered 30"]} tone="warn">
              <span className="grid h-9 w-14 place-items-center rounded-control border-2 border-brand-500 bg-surface font-numeric font-semibold">
                28
              </span>
            </TableRow>
          </div>
        ),
      },
      {
        words: { en: "Type the invoice number." },
        where: RECEIVE_ORDER("invoice"),
        picture: <Field label="Their invoice or delivery note number" value="INV-5521" wide />,
      },
      {
        words: { en: "Tap Receive. The stock is on the shelf." },
        where: RECEIVE_ORDER("receive"),
        picture: (
          <Tap>
            <Btn variant="primary">Receive R1 284.00</Btn>
          </Tap>
        ),
      },
    ],
  },
  {
    id: "receive-stock",
    title: { en: "Stock with no order" },
    icon: PackagePlus,
    tone: "money",
    steps: [
      {
        words: { en: "Open Inventory. Tap ⋯ next to the product." },
        where: office(INVENTORY, "row-action"),
        picture: (
          <TableRow cells={["Maize meal 10kg", "4 left"]}>
            <Tap>
              <Btn size="sm" icon={<Ellipsis className="size-4" />} aria-label="More actions" />
            </Tap>
          </TableRow>
        ),
      },
      { words: { en: "Tap Receive stock." }, picture: <ProductMenu pick="Receive stock" /> },
      {
        words: { en: "Type how many came, and the cost of one." },
        where: RECEIVE_STOCK("qty"),
        picture: (
          <div className="grid justify-items-center gap-3">
            <Field label="Quantity received" value="24" />
            <Field label="Cost per unit" value="9.50" />
          </div>
        ),
      },
      {
        words: { en: "Tap Receive." },
        where: RECEIVE_STOCK("go"),
        picture: (
          <Tap>
            <Btn variant="primary">Receive</Btn>
          </Tap>
        ),
      },
    ],
  },
  {
    id: "write-off",
    title: { en: "Broken or expired" },
    icon: PackageX,
    tone: "money",
    steps: [
      {
        words: { en: "Tap ⋯ next to the product. Tap Adjust stock." },
        where: office(INVENTORY, "row-action"),
        picture: <ProductMenu pick="Adjust stock" />,
      },
      {
        words: { en: "Type minus how many. Like −2." },
        where: ADJUST("change"),
        picture: <Field label="Change in quantity" value="-2" />,
      },
      {
        words: { en: "Choose Wastage, breakage or expiry. Say what happened." },
        where: ADJUST("type"),
        picture: <Field label="Type" value="Wastage, breakage or expiry" wide />,
      },
      {
        words: { en: "Tap Adjust." },
        where: ADJUST("adjust"),
        picture: (
          <Tap>
            <Btn variant="primary">Adjust</Btn>
          </Tap>
        ),
      },
    ],
  },
  {
    id: "count-shelf",
    title: { en: "Count the shelf" },
    icon: ClipboardList,
    tone: "money",
    steps: [
      {
        words: { en: "Count what is really on the shelf." },
        picture: (
          <Big tone="money">
            <ClipboardList />
          </Big>
        ),
      },
      {
        words: { en: "Tap ⋯ next to the product. Tap Count stock." },
        where: office(INVENTORY, "row-action"),
        picture: <ProductMenu pick="Count stock" />,
      },
      {
        words: { en: "Type what you counted." },
        where: COUNT("counted"),
        picture: <Field label="Counted on the shelf" value="17" />,
      },
      {
        words: { en: "Tap Save the count. The difference is fixed." },
        where: COUNT("save"),
        picture: (
          <Tap>
            <Btn variant="primary">Save the count</Btn>
          </Tap>
        ),
      },
    ],
  },
  {
    id: "what-to-order",
    title: { en: "What to order" },
    icon: TrendingDown,
    tone: "money",
    steps: [
      {
        words: { en: "Open Inventory." },
        where: office(INVENTORY, "nav"),
        picture: (
          <Tap>
            <NavItem label="Inventory" />
          </Tap>
        ),
      },
      {
        words: { en: "Look at Top low stock items. Order these." },
        where: office(INVENTORY, "panel"),
        picture: (
          <div className="grid w-full max-w-[19rem] gap-2">
            <TableRow cells={["Maize meal 10kg", "Low stock", "2 left"]} tone="warn" />
            <TableRow cells={["Sunflower oil 2L", "Out of stock", "0 left"]} tone="danger" />
          </div>
        ),
      },
      {
        words: { en: "Or choose Low stock under Stock status." },
        where: office(INVENTORY, "filter:Stock status"),
        picture: <Field label="Stock status" value="Low stock" />,
      },
    ],
  },
  {
    id: "import-products",
    title: { en: "Load products from Excel" },
    icon: FileSpreadsheet,
    tone: "money",
    steps: [
      {
        words: { en: "Open Inventory. Tap Import products." },
        where: office(INVENTORY, "action:import"),
        picture: (
          <Tap>
            <Btn icon={<Upload className="size-4" />}>Import products</Btn>
          </Tap>
        ),
      },
      {
        words: { en: "Drop your spreadsheet in the box." },
        picture: (
          <span className="grid w-full max-w-[17rem] justify-items-center gap-2 rounded-card border-2 border-dashed border-brand-500 bg-brand-50 px-4 py-6 text-sm font-semibold text-brand-600">
            <FileSpreadsheet className="size-10" />
            Drop your spreadsheet here
          </span>
        ),
      },
      {
        words: { en: "Check the report. Fix the red rows." },
        picture: (
          <div className="grid w-full max-w-[19rem] gap-2">
            <TableRow cells={["Ready", "", "412"]} />
            <TableRow cells={["With warnings", "", "3"]} tone="warn" />
            <TableRow cells={["Cannot import", "", "1"]} tone="danger" />
          </div>
        ),
      },
      {
        words: { en: "Tap Import. Then count the stock in." },
        picture: (
          <Tap>
            <Btn variant="primary">Import 412 products</Btn>
          </Tap>
        ),
      },
    ],
  },
];

// ── Owner ───────────────────────────────────────────────────────────────────

const ADD_SOMEONE = form(
  USERS,
  "Add someone",
  [
    { id: "first", label: "First name", value: "Thabo" },
    { id: "code", label: "Staff code", value: "THABO" },
    { id: "pin", label: "PIN (optional)", kind: "pin" },
    { id: "role", label: "Role", value: "Cashier" },
    { id: "scope", label: "Applies to", value: "The whole business" },
  ],
  [{ id: "add", label: "Add", tone: "brand" }],
);

const REGISTER = form(
  DEVICES,
  "Register a terminal",
  [
    { id: "name", label: "Terminal name", value: "Front counter" },
    { id: "branch", label: "Branch", value: "Main" },
    { id: "till", label: "Till", value: "Till 1" },
  ],
  [{ id: "register", label: "Register", tone: "brand" }],
);

const CREDIT = form(
  CUSTOMERS,
  "Mrs Dlamini",
  [
    { id: "paid", label: "Payment received" },
    { id: "limit", label: "Credit limit", value: "300.00" },
    { id: "reason", label: "Reason", value: "Regular customer" },
  ],
  [{ id: "set", label: "Set limit", tone: "brand" }],
);

const RULES = form(
  BUSINESS,
  "Trading rules",
  [
    { id: "negative", label: "When stock would go negative", value: "Allow the sale and flag it" },
    { id: "limit", label: "Largest refund with no signal (R)", value: "200" },
    { id: "approval", label: "Refunds need manager approval", kind: "toggle" },
  ],
  [{ id: "save", label: "Save changes", tone: "brand" }],
);

const RECORD_COST = form(
  EXPENSES,
  "Record a cost",
  [
    { id: "kind", label: "What kind of cost?", value: "Electricity" },
    { id: "amount", label: "Amount", value: "850.00" },
    { id: "what", label: "What was it for?", value: "Prepaid units" },
    { id: "date", label: "Date paid", value: "15 Oct" },
  ],
  [{ id: "record", label: "Record", tone: "brand" }],
);

export const OWNER_TASKS: Task[] = [
  {
    id: "add-staff",
    title: { en: "Add a staff member" },
    icon: UserPlus,
    tone: "navy",
    steps: [
      {
        words: { en: "Open Settings, then Users. Tap Add someone." },
        where: office(USERS, "action:add"),
        picture: (
          <Tap>
            <Btn variant="primary" icon={<UserPlus className="size-4" />}>
              Add someone
            </Btn>
          </Tap>
        ),
      },
      {
        words: { en: "Type their name and a staff code." },
        where: ADD_SOMEONE("code"),
        picture: (
          <div className="grid justify-items-center gap-3">
            <Field label="First name" value="Thabo" />
            <Field label="Staff code" value="THABO" />
          </div>
        ),
      },
      {
        words: { en: "Let them type their own PIN." },
        where: ADD_SOMEONE("pin"),
        picture: (
          <div className="grid justify-items-center gap-3">
            <PinDots filled={4} />
            <Keypad />
          </div>
        ),
      },
      {
        words: { en: "Choose their role. Tap Add." },
        where: ADD_SOMEONE("role"),
        picture: (
          <div className="grid justify-items-center gap-4">
            <Field label="Role" value="Cashier" />
            <Tap>
              <Btn variant="primary">Add</Btn>
            </Tap>
          </div>
        ),
      },
      {
        words: { en: "They sign in once on each till, with signal." },
        picture: <Chip online />,
      },
    ],
  },
  {
    id: "connect-till",
    title: { en: "Connect a new till" },
    icon: MonitorSmartphone,
    tone: "navy",
    steps: [
      {
        words: { en: "Open Settings, then Tills & devices. Tap Register a terminal." },
        where: office(DEVICES, "action:register"),
        picture: (
          <Tap>
            <Btn variant="primary">Register a terminal</Btn>
          </Tap>
        ),
      },
      {
        words: { en: "Name it and choose its till. Tap Register." },
        where: REGISTER("name"),
        picture: (
          <div className="grid justify-items-center gap-3">
            <Field label="Terminal name" value="Front counter" />
            <Field label="Till" value="Till 1" />
          </div>
        ),
      },
      {
        words: { en: "Six numbers appear. They work for 15 minutes." },
        picture: (
          <span className="rounded-card bg-gold-50 px-6 py-4 font-numeric text-4xl font-extrabold tracking-[0.2em] text-navy-900">
            482913
          </span>
        ),
      },
      {
        words: { en: "On the new till, type the numbers. Tap Register terminal." },
        picture: (
          <div className="grid justify-items-center gap-4">
            <Field label="Pairing code" value="482913" />
            <Tap>
              <Btn variant="primary">Register terminal</Btn>
            </Tap>
          </div>
        ),
      },
    ],
  },
  {
    id: "lost-till",
    title: { en: "A till is lost" },
    icon: ShieldOff,
    tone: "danger",
    steps: [
      {
        words: { en: "Open Settings. Tap Tills & devices." },
        where: office(DEVICES, "tab:Tills & devices"),
        picture: (
          <Tap>
            <TabPill label="Tills & devices" />
          </Tap>
        ),
      },
      {
        words: { en: "Tap Suspend next to the lost till." },
        where: office(DEVICES, "row-action"),
        picture: (
          <TableRow cells={["Takeaway", "Last seen yesterday"]}>
            <Tap>
              <Btn size="sm" variant="danger">
                Suspend
              </Btn>
            </Tap>
          </TableRow>
        ),
      },
      {
        words: { en: "Nobody can use it now. Connect a new one." },
        picture: (
          <Verdict ok={false}>
            <Big tone="danger">
              <Smartphone />
            </Big>
          </Verdict>
        ),
      },
    ],
  },
  {
    id: "give-credit",
    title: { en: "Give a customer credit" },
    icon: BookUser,
    tone: "navy",
    steps: [
      {
        words: { en: "Open Customers. Tap the customer." },
        where: office(CUSTOMERS, "rows"),
        picture: (
          <Tap>
            <TableRow cells={["Mrs Dlamini", "072 555 0134", "Cash only"]} />
          </Tap>
        ),
      },
      {
        words: { en: "Type their limit. Start small." },
        where: CREDIT("limit"),
        picture: <Field label="Credit limit" value="300.00" />,
      },
      {
        words: { en: "Tap Set limit. Now they can buy on account." },
        where: CREDIT("set"),
        picture: (
          <Tap>
            <Btn variant="primary">Set limit</Btn>
          </Tap>
        ),
      },
    ],
  },
  {
    id: "approve-order",
    title: { en: "Approve an order" },
    icon: CheckCheck,
    tone: "navy",
    steps: [
      {
        words: { en: "Open Procurement." },
        where: office(ORDERS_TO_APPROVE, "nav"),
        picture: (
          <Tap>
            <NavItem label="Procurement" />
          </Tap>
        ),
      },
      {
        words: { en: "Check the order. Tap Approve." },
        where: office(ORDERS_TO_APPROVE, "row-action"),
        picture: (
          <TableRow cells={["PO-0013 · Draft", "Coca-Cola SA · R1 284.00"]}>
            <Tap>
              <Btn size="sm" variant="primary">
                Approve
              </Btn>
            </Tap>
          </TableRow>
        ),
      },
    ],
  },
  {
    id: "shop-rules",
    title: { en: "Set the shop's rules" },
    icon: SlidersHorizontal,
    tone: "navy",
    steps: [
      {
        words: { en: "Open Settings. Tap Business." },
        where: office(BUSINESS, "tab:Business"),
        picture: (
          <Tap>
            <TabPill label="Business" />
          </Tap>
        ),
      },
      {
        words: { en: "Choose the biggest refund allowed with no signal." },
        where: RULES("limit"),
        picture: <Field label="Largest refund with no signal (R)" value="200" wide />,
      },
      {
        words: { en: "Want to approve every refund? Turn this on." },
        where: RULES("approval"),
        picture: <Toggle />,
      },
      {
        words: { en: "Tap Save changes." },
        where: RULES("save"),
        picture: (
          <Tap>
            <Btn variant="primary">Save changes</Btn>
          </Tap>
        ),
      },
    ],
  },
  {
    id: "how-doing",
    title: { en: "How is the shop doing?" },
    icon: ChartColumn,
    tone: "navy",
    steps: [
      {
        words: { en: "Open Dashboard. See today, this week and this month." },
        where: office(DASHBOARD, "kpis"),
        picture: (
          <Row>
            {[
              ["Today", "R2 140"],
              ["This week", "R14 980"],
              ["This month", "R61 300"],
            ].map(([label, value]) => (
              <span
                key={label}
                className="grid rounded-card border border-line bg-surface px-3 py-2 text-left shadow-card"
              >
                <span className="text-xs text-ink-500">{label}</span>
                <span className="font-numeric text-lg font-bold text-ink-900">{value}</span>
              </span>
            ))}
          </Row>
        ),
      },
      {
        words: { en: "Open Reports. Choose the dates." },
        where: office(REPORTS, "filter:From"),
        picture: (
          <Row>
            <Field label="From" value="1 Oct" />
            <Field label="To" value="15 Oct" />
          </Row>
        ),
      },
      {
        words: { en: "Tap Spreadsheet to keep a copy." },
        where: office(REPORTS, "row-action"),
        picture: (
          <Tap>
            <Btn icon={<FileSpreadsheet className="size-4" />}>Spreadsheet</Btn>
          </Tap>
        ),
      },
    ],
  },
  {
    id: "record-cost",
    title: { en: "Record a cost" },
    icon: Wallet,
    tone: "navy",
    steps: [
      {
        words: { en: "Open Expenses. Tap Record a cost." },
        where: office(EXPENSES, "action:record"),
        picture: (
          <Tap>
            <Btn variant="primary">Record a cost</Btn>
          </Tap>
        ),
      },
      {
        words: { en: "Choose what kind. Type the amount." },
        where: RECORD_COST("amount"),
        picture: (
          <div className="grid justify-items-center gap-3">
            <Field label="What kind of cost?" value="Electricity" />
            <Field label="Amount" value="850.00" />
          </div>
        ),
      },
      {
        words: { en: "Tap Record." },
        where: RECORD_COST("record"),
        picture: (
          <Tap>
            <Btn variant="primary">Record</Btn>
          </Tap>
        ),
      },
    ],
  },
  {
    id: "someone-leaves",
    title: { en: "Someone leaves" },
    icon: UserMinus,
    tone: "danger",
    steps: [
      {
        words: { en: "Open Settings. Tap Users." },
        where: office(USERS_LEAVING, "tab:Users"),
        picture: (
          <Tap>
            <TabPill label="Users" />
          </Tap>
        ),
      },
      {
        words: { en: "Tap Deactivate. Do it the same day." },
        where: office(USERS_LEAVING, "row-action"),
        picture: (
          <TableRow cells={["Thabo Nkosi", "Cashier"]}>
            <Tap>
              <Btn size="sm" variant="danger">
                Deactivate
              </Btn>
            </Tap>
          </TableRow>
        ),
      },
      {
        words: { en: "Their old sales keep their name." },
        picture: (
          <Verdict ok>
            <Basket lines={[["Sold by Thabo", 3, "R54.00"]]} total="R54.00" />
          </Verdict>
        ),
      },
    ],
  },
];
